/**
 * Supabase Client Utility
 * 
 * This module provides a singleton instance of the Supabase client
 * that can be imported and used throughout the application.
 * Includes enhanced error handling and retry logic.
 */

import { createClient } from '@supabase/supabase-js';

// Initialize the Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check if environment variables are properly loaded
if (!supabaseUrl || !supabaseKey) {
  console.error('Supabase environment variables are missing!');
  
  // Log available env variables for debugging
  console.log('Available env variables:', Object.keys(process.env)
    .filter(key => key.startsWith('REACT_APP_'))
    .reduce((obj, key) => {
      obj[key] = process.env[key] ? '[SET]' : '[NOT SET]';
      return obj;
    }, {})
  );
}

// Track request timestamps to prevent excessive requests
const requestTimestamps = new Map();
const REQUEST_LIMIT = 5; // Max requests per 5 seconds
const REQUEST_WINDOW = 5000; // 5 seconds window

// Create client options with retries and timeouts
const options = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    fetch: (...args) => {
      // Implement rate limiting to prevent excessive requests
      const url = args[0].toString();
      const now = Date.now();
      
      // Clean up old request timestamps
      for (const [storedUrl, timestamps] of requestTimestamps.entries()) {
        requestTimestamps.set(
          storedUrl, 
          timestamps.filter(timestamp => now - timestamp < REQUEST_WINDOW)
        );
      }
      
      // Check if we've made too many requests recently
      const timestamps = requestTimestamps.get(url) || [];
      if (timestamps.length >= REQUEST_LIMIT) {
        console.warn(`Too many requests to ${url}, throttling`);
        return Promise.reject(new Error('Request throttled to prevent excessive API calls'));
      }
      
      // Record this request
      requestTimestamps.set(url, [...timestamps, now]);
      
      // Use custom fetch with timeout
      return new Promise((resolve, reject) => {
        // Set timeout to 25 seconds (increased from 15)
        const timeout = setTimeout(() => {
          reject(new Error('Request timed out'));
        }, 25000);
        
        fetch(...args)
          .then(response => {
            clearTimeout(timeout);
            resolve(response);
          })
          .catch(error => {
            clearTimeout(timeout);
            reject(error);
          });
      });
    }
  }
};

// Create a single instance of the Supabase client
const supabase = createClient(supabaseUrl, supabaseKey, options);

// Add event listener for online/offline status
window.addEventListener('online', () => {
  console.log('Back online, reconnecting to Supabase...');
  // Force auth refresh on reconnect
  supabase.auth.refreshSession();
});

// Exponential backoff helper function
const backoff = (attempt) => {
  const baseDelay = 1000; // Start with 1 second
  const maxDelay = 10000; // Max 10 seconds
  return Math.min(Math.pow(2, attempt) * baseDelay, maxDelay);
};

// Export a wrapped version of supabase with enhanced error handling
const enhancedSupabase = {
  auth: {
    ...supabase.auth,
    // Ensure onAuthStateChange is explicitly added
    onAuthStateChange: (...args) => supabase.auth.onAuthStateChange(...args),
    // Add signInWithOtp method
    signInWithOtp: (...args) => supabase.auth.signInWithOtp(...args),
    // Add verifyOtp method
    verifyOtp: (...args) => supabase.auth.verifyOtp(...args),
    // Override getSession with retry logic and improved backoff
    getSession: async () => {
      let attempt = 0;
      const maxAttempts = 2; // Max 2 attempts
      
      while (attempt < maxAttempts) {
        try {
          const result = await supabase.auth.getSession();
          return result;
        } catch (error) {
          attempt++;
          console.error(`Error getting session (attempt ${attempt}/${maxAttempts}):`, error);
          
          if (attempt >= maxAttempts) {
            console.error('Failed to get session after max retries');
            // Return empty result instead of throwing
            return { data: { session: null }, error: error };
          }
          
          // Wait with exponential backoff before retry
          const delay = backoff(attempt);
          console.log(`Retrying in ${delay/1000} seconds...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
  },
  // Pass through other methods directly
  from: (...args) => supabase.from(...args),
  storage: supabase.storage,
  rpc: supabase.rpc,
  channel: supabase.channel
};

export default enhancedSupabase; 